/**
 * Simple LRU Cache Manager
 * Manages cache groups with independent LRU eviction policies
 */

import { create } from '@thoughtspot/logger';
import * as localForage from 'localforage';

const logger = create('LruCacheManager');

export const LRU_METADATA_KEY = '__lru_metadata__';
const PERSIST_DEBOUNCE_MS = 1000; // 1 second debounce

export interface CacheGroupConfig {
    name: string;
    keyPattern: RegExp;
    extractEntityId: (key: string) => string | null;
    maxEntities: number;
    ttlMs: number; // Time-to-live for cache entries in this group
}

interface CacheKeyMetadata {
    createdAt: number;      // When this cache key was first created
    lastUpdated: number;    // When this cache key was last updated (written)
}

interface EntityMetadata {
    lastAccessed: number;   // Most recent access across all cache keys
    cacheKeys: Set<string>; // All cache keys for this entity
    cacheKeyMetadata: Map<string, CacheKeyMetadata>; // Per-key metadata
}

// Serializable version for IndexedDB
interface SerializedCacheKeyMetadata {
    createdAt: number;
    lastUpdated: number;
}

interface SerializedEntityMetadata {
    lastAccessed: number;
    cacheKeys: string[];
    cacheKeyMetadata: Record<string, SerializedCacheKeyMetadata>;
}

interface SerializedMetadata {
    version: number;
    groups: Record<string, Record<string, SerializedEntityMetadata>>;
}

/**
 * Simple in-memory LRU manager for cache groups with IndexedDB persistence
 */
export class LruCacheManager {
    private static instance: LruCacheManager;

    private groups: Map<string, CacheGroupConfig> = new Map();

    private entities: Map<string, Map<string, EntityMetadata>> = new Map(); // groupName -> entityId -> metadata

    private persistTimer: ReturnType<typeof setTimeout> | null = null;

    private initialized = false;

    // eslint-disable-next-line @typescript-eslint/no-empty-function, no-useless-constructor
    private constructor() {}

    static getInstance(): LruCacheManager {
        if (!LruCacheManager.instance) {
            LruCacheManager.instance = new LruCacheManager();
        }
        return LruCacheManager.instance;
    }

    /**
     * Register a cache group
     */
    registerCacheGroup(config: CacheGroupConfig): void {
        this.groups.set(config.name, config);
        if (!this.entities.has(config.name)) {
            this.entities.set(config.name, new Map());
        }
    }

    /**
     * Match cache key to group and extract entity ID
     */
    matchCacheKey(cacheKey: string): { groupName: string; entityId: string } | null {
        let result: { groupName: string; entityId: string } | null = null;
        this.groups.forEach((config, groupName) => {
            if (!result && config.keyPattern.test(cacheKey)) {
                const entityId = config.extractEntityId(cacheKey);
                if (entityId) {
                    result = { groupName, entityId };
                }
            }
        });
        return result;
    }

    /**
     * Record access to an entity (update timestamp for LRU tracking)
     * Note: This only updates entity-level lastAccessed for LRU eviction.
     * Cache key lastUpdated is only updated when data is written via trackCacheKey.
     */
    recordAccess(groupName: string, entityId: string): void {
        logger.debug(`[LruCacheManager] Recording access to entity "${entityId}" in group "${groupName}"`);
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return;

        const metadata = groupEntities.get(entityId);
        if (metadata) {
            metadata.lastAccessed = Date.now();
            this.debouncedPersist();
        }
    }

    /**
     * Track a cache key for an entity (called when cache is written)
     * Updates lastUpdated timestamp to track when data was last refreshed
     */
    trackCacheKey(groupName: string, entityId: string, cacheKey: string): void {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return;

        const now = Date.now();
        let metadata = groupEntities.get(entityId);
        if (!metadata) {
            metadata = {
                lastAccessed: now,
                cacheKeys: new Set(),
                cacheKeyMetadata: new Map(),
            };
            groupEntities.set(entityId, metadata);
        }

        // Add cache key to set
        metadata.cacheKeys.add(cacheKey);

        // Update or create cache key metadata
        if (!metadata.cacheKeyMetadata.has(cacheKey)) {
            metadata.cacheKeyMetadata.set(cacheKey, {
                createdAt: now,
                lastUpdated: now,
            });
        } else {
            const keyMeta = metadata.cacheKeyMetadata.get(cacheKey)!;
            keyMeta.lastUpdated = now;
        }

        // Update entity-level lastAccessed
        metadata.lastAccessed = now;
        this.debouncedPersist();
    }

    /**
     * Remove a cache key from an entity
     */
    removeCacheKey(groupName: string, entityId: string, cacheKey: string): void {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return;

        const metadata = groupEntities.get(entityId);
        if (metadata) {
            metadata.cacheKeys.delete(cacheKey);
            metadata.cacheKeyMetadata.delete(cacheKey);

            // Remove entity if no keys left
            if (metadata.cacheKeys.size === 0) {
                groupEntities.delete(entityId);
            }
            this.debouncedPersist();
        }
    }

    /**
     * Remove an entity completely
     */
    removeEntity(groupName: string, entityId: string): void {
        const groupEntities = this.entities.get(groupName);
        if (groupEntities) {
            groupEntities.delete(entityId);
            this.debouncedPersist();
        }
    }

    /**
     * Get least recently used entity in a group
     */
    getLeastRecentlyUsed(groupName: string): string | null {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities || groupEntities.size === 0) {
            return null;
        }

        let lruEntityId: string | null = null;
        let oldestTime = Infinity;

        groupEntities.forEach((metadata, entityId) => {
            if (metadata.lastAccessed < oldestTime) {
                oldestTime = metadata.lastAccessed;
                lruEntityId = entityId;
            }
        });

        return lruEntityId;
    }

    /**
     * Get entity count for a group
     */
    getEntityCount(groupName: string): number {
        const groupEntities = this.entities.get(groupName);
        return groupEntities ? groupEntities.size : 0;
    }

    /**
     * Check if entity exists
     */
    hasEntity(groupName: string, entityId: string): boolean {
        const groupEntities = this.entities.get(groupName);
        return groupEntities ? groupEntities.has(entityId) : false;
    }

    /**
     * Get cache keys for an entity (returns defensive copy)
     */
    getCacheKeys(groupName: string, entityId: string): Set<string> | null {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return null;

        const metadata = groupEntities.get(entityId);
        return metadata ? new Set(metadata.cacheKeys) : null;
    }

    /**
     * Get the oldest cache update time for an entity
     * Returns the oldest lastUpdated timestamp across all cache keys
     * that are within the last 24 hours, along with isMixedTimeData flag.
     * isMixedTimeData is true when the difference between newest and oldest
     * valid cache times exceeds 1 hour, indicating data from multiple timestamps.
     * Returns null if entity not found, has no cache keys, or all keys are older than 24 hours
     */
    getOldestCacheTime(groupName: string, entityId: string): { oldestTime: number | null; isMixedTimeData: boolean } {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return { oldestTime: null, isMixedTimeData: false };

        const metadata = groupEntities.get(entityId);
        if (!metadata || metadata.cacheKeyMetadata.size === 0) {
            return { oldestTime: null, isMixedTimeData: false };
        }

        const maxCacheAgeMs = 24 * 60 * 60 * 1000;
        const mixedThresholdMs = 60 * 60 * 1000;
        const cutoff = Date.now() - maxCacheAgeMs;
        let oldestTime = Infinity;
        let newestTime = -Infinity;
        metadata.cacheKeyMetadata.forEach((keyMeta) => {
            if (keyMeta.lastUpdated >= cutoff) {
                if (keyMeta.lastUpdated < oldestTime) {
                    oldestTime = keyMeta.lastUpdated;
                }
                if (keyMeta.lastUpdated > newestTime) {
                    newestTime = keyMeta.lastUpdated;
                }
            }
        });

        if (oldestTime === Infinity) return { oldestTime: null, isMixedTimeData: false };

        return {
            oldestTime,
            isMixedTimeData: (newestTime - oldestTime) > mixedThresholdMs,
        };
    }

    /**
     * Get metadata for a specific cache key
     * Returns null if not found
     */
    getCacheKeyMetadata(groupName: string, entityId: string, cacheKey: string): CacheKeyMetadata | null {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return null;

        const metadata = groupEntities.get(entityId);
        if (!metadata) return null;

        return metadata.cacheKeyMetadata.get(cacheKey) || null;
    }

    /**
     * Check if eviction is needed
     */
    shouldEvict(groupName: string, entityId: string): boolean {
        const config = this.groups.get(groupName);
        if (!config) return false;

        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return false;

        const entityExists = groupEntities.has(entityId);

        // Check if adding this entity would exceed capacity
        return !entityExists && groupEntities.size >= config.maxEntities;
    }

    /**
     * Get max capacity for a group
     */
    getMaxCapacity(groupName: string): number {
        const config = this.groups.get(groupName);
        return config ? config.maxEntities : 0;
    }

    /**
     * Get entity statistics for a group
     * Returns array of { entityId, keyCount } for all entities in the group
     */
    getGroupEntityStats(groupName: string): Array<{ entityId: string; keyCount: number }> {
        const groupEntities = this.entities.get(groupName);
        if (!groupEntities) return [];

        const stats: Array<{ entityId: string; keyCount: number }> = [];
        groupEntities.forEach((metadata, entityId) => {
            stats.push({
                entityId,
                keyCount: metadata.cacheKeys.size,
            });
        });

        // Sort by key count descending
        return stats.sort((a, b) => b.keyCount - a.keyCount);
    }

    /**
     * Get TTL for a group
     */
    getTtl(groupName: string): number | null {
        const config = this.groups.get(groupName);
        return config ? config.ttlMs : null;
    }

    /**
     * Serialize entities Map to plain objects for IndexedDB storage
     */
    private serialize(): SerializedMetadata {
        const groups: Record<string, Record<string, SerializedEntityMetadata>> = {};

        this.entities.forEach((groupEntities, groupName) => {
            groups[groupName] = {};
            groupEntities.forEach((metadata, entityId) => {
                // Serialize cacheKeyMetadata Map to plain object
                const cacheKeyMetadata: Record<string, SerializedCacheKeyMetadata> = {};
                metadata.cacheKeyMetadata.forEach((keyMeta, cacheKey) => {
                    cacheKeyMetadata[cacheKey] = {
                        createdAt: keyMeta.createdAt,
                        lastUpdated: keyMeta.lastUpdated,
                    };
                });

                groups[groupName][entityId] = {
                    lastAccessed: metadata.lastAccessed,
                    cacheKeys: Array.from(metadata.cacheKeys),
                    cacheKeyMetadata,
                };
            });
        });

        return {
            version: 1,
            groups,
        };
    }

    /**
     * Deserialize plain objects back to entities Map
     */
    private deserialize(data: SerializedMetadata): void {
        if (data.version !== 1) {
            logger.warn('[LruCacheManager] Unknown metadata version, ignoring persisted data');
            return;
        }

        this.entities.clear();

        Object.entries(data.groups).forEach(([groupName, groupData]) => {
            const groupEntities = new Map<string, EntityMetadata>();

            Object.entries(groupData).forEach(([entityId, metadata]) => {
                // Deserialize cacheKeyMetadata from plain object to Map
                const cacheKeyMetadata = new Map<string, CacheKeyMetadata>();
                if (metadata.cacheKeyMetadata) {
                    Object.entries(metadata.cacheKeyMetadata).forEach(([cacheKey, keyMeta]) => {
                        cacheKeyMetadata.set(cacheKey, {
                            createdAt: keyMeta.createdAt,
                            lastUpdated: keyMeta.lastUpdated,
                        });
                    });
                }

                groupEntities.set(entityId, {
                    lastAccessed: metadata.lastAccessed,
                    cacheKeys: new Set(metadata.cacheKeys),
                    cacheKeyMetadata,
                });
            });

            this.entities.set(groupName, groupEntities);
        });
    }

    /**
     * Persist metadata to IndexedDB
     */
    private async persist(): Promise<void> {
        try {
            const serialized = this.serialize();
            await localForage.setItem(LRU_METADATA_KEY, serialized);
        } catch (error) {
            logger.error('[LruCacheManager] Failed to persist metadata:', error);
        }
    }

    /**
     * Schedule debounced persistence
     */
    private debouncedPersist(): void {
        if (this.persistTimer) {
            clearTimeout(this.persistTimer);
        }

        this.persistTimer = setTimeout(() => {
            this.persist();
            this.persistTimer = null;
        }, PERSIST_DEBOUNCE_MS);
    }

    /**
     * Load metadata from IndexedDB
     */
    private async load(): Promise<void> {
        try {
            const data = await localForage.getItem<SerializedMetadata>(LRU_METADATA_KEY);
            if (data) {
                this.deserialize(data);
                logger.debug('[LruCacheManager] Loaded metadata from IndexedDB');
            }
        } catch (error) {
            logger.error('[LruCacheManager] Failed to load metadata:', error);
        }
    }

    /**
     * Initialize LRU manager (load persisted metadata)
     */
    async initialize(): Promise<void> {
        if (this.initialized) return;

        await this.load();
        this.initialized = true;
    }

    /**
     * Clear all metadata (for testing)
     */
    clearAll(): void {
        this.entities.clear();
        this.groups.forEach((_, groupName) => {
            this.entities.set(groupName, new Map());
        });
        this.debouncedPersist();
    }
}
